import React, { useState } from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
  Paper,
  Typography,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  Trash2 as DeleteIcon,
  Copy as ContentCopyIcon,
  Webhook as WebhookOutlinedIcon,
  Info as InfoIcon,
  Lock as LockIcon,
  UsersRound as UsersIcon,
} from 'lucide-mui';
import type { Draft } from 'immer';
import DeleteDialog from './DeleteDialog';
import { Webhook } from '../../../../../types/state';
import LearnMore from './LearnMore';
import { ToggleSetting } from '../../../common';

type WebhookWithType = Draft<Webhook> & { type: 'team' | 'user' };

interface WebhooksProps {
  webhooks: WebhookWithType[];
  onRemoveWebhook: (url: string, type: 'team' | 'user') => Promise<void>;
  note?: React.ReactNode;
  hideLearnMore?: boolean;
  emptyStateButton?: React.ReactNode;
}

const SNACKBAR_DURATION = 4000;

const Webhooks: React.FC<WebhooksProps> = ({
  webhooks,
  onRemoveWebhook,
  note,
  hideLearnMore,
  emptyStateButton,
}) => {
  const [formState, setFormState] = useState({
    snackbarOpen: false,
    snackbarMessage: '',
    deleteDialogOpen: false,
    webhookToDelete: null as WebhookWithType | null,
  });

  const [learnMoreOpen, setLearnMoreOpen] = useState(false);

  const updateFormState = (updates: Partial<typeof formState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  };

  const handleRemoveWebhook = (webhook: WebhookWithType) => {
    updateFormState({
      webhookToDelete: webhook,
      deleteDialogOpen: true,
    });
  };

  const handleConfirmDelete = async () => {
    const { webhookToDelete } = formState;
    if (!webhookToDelete) return;

    try {
      await onRemoveWebhook(webhookToDelete.url, webhookToDelete.type);
      updateFormState({
        snackbarMessage: 'Webhook removed successfully',
        snackbarOpen: true,
        deleteDialogOpen: false,
        webhookToDelete: null,
      });
    } catch (error) {
      updateFormState({
        snackbarMessage: `Failed to remove webhook: ${error.message}`,
        snackbarOpen: true,
      });
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    updateFormState({
      snackbarMessage: `${label} copied to clipboard`,
      snackbarOpen: true,
    });
  };

  const handleClose = (key: keyof typeof formState) => {
    updateFormState({ [key]: false });
  };

  const getWebhookIcon = (type: 'team' | 'user') => {
    const iconProps = {
      size: 16,
      strokeWidth: 2,
      className: 'icon',
    };

    if (type === 'team') {
      return <UsersIcon {...iconProps} />;
    }
    return <LockIcon {...iconProps} />;
  };

  return (
    <Box sx={{ py: 2 }}>
      {!hideLearnMore && (
        <ToggleSetting
          checked={false}
          onChange={() => setLearnMoreOpen(true)}
          label="Learn More"
          description="Send videos, transcripts, and AI summaries directly to Slack, Teams, or your favorite tools — perfect for sharing insights with your team"
          icon={<InfoIcon />}
          rightElement={
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => setLearnMoreOpen(true)}
            >
              Learn More
            </Button>
          }
        />
      )}

      {!hideLearnMore && (
        <LearnMore
          open={learnMoreOpen}
          onClose={() => setLearnMoreOpen(false)}
          note={note}
        />
      )}

      {webhooks.length > 0 ? (
        <Paper variant="outlined" sx={{ borderColor: 'divider', mt: 3 }}>
          <List disablePadding>
            {webhooks.map((webhook, index) => (
              <React.Fragment key={webhook.url}>
                <ListItem
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        onClick={() => handleRemoveWebhook(webhook)}
                        color="error"
                        size="small"
                        sx={{
                          opacity: { xs: 1, sm: 0 },
                          transition: 'opacity 0.2s',
                          '.MuiListItem-root:hover &': {
                            opacity: 1,
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  }
                  sx={{
                    py: { xs: 3, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          flexDirection: { xs: 'column', sm: 'row' },
                          pr: { xs: 4, sm: 0 },
                        }}
                      >
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            flexShrink: 0,
                            '& .icon': {
                              color: '#fff',
                              stroke: '#fff',
                            },
                          }}
                        >
                          {getWebhookIcon(webhook.type)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.975rem',
                              lineHeight: 1.3,
                              color: 'text.primary',
                              mb: 0.5,
                            }}
                            noWrap
                          >
                            {webhook.name}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              pr: { xs: 0, sm: 8 },
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                wordBreak: 'break-all',
                                cursor: 'pointer',
                                color: 'text.disabled',
                                transition: 'color 0.2s',
                                '&:hover': {
                                  color: 'primary.main',
                                },
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                maxWidth: '100%',
                              }}
                              onClick={() =>
                                handleCopy(webhook.url, 'Webhook URL')
                              }
                            >
                              {webhook.url}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleCopy(webhook.url, 'Webhook URL')
                              }
                              sx={{
                                opacity: { xs: 1, sm: 0 },
                                transition: 'opacity 0.2s',
                                '.MuiListItem-root:hover &': {
                                  opacity: 1,
                                },
                                p: 0.5,
                              }}
                            >
                              <ContentCopyIcon sx={{ width: 14, height: 14 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < webhooks.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 6,
            mt: 3,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <WebhookOutlinedIcon
            sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
          />
          <Typography
            color="text.secondary"
            sx={{ mb: emptyStateButton ? 3 : 2 }}
          >
            No webhooks configured yet
          </Typography>
          {emptyStateButton}
        </Box>
      )}

      <Snackbar
        open={formState.snackbarOpen}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={() => handleClose('snackbarOpen')}
        message={formState.snackbarMessage}
      />

      <DeleteDialog
        open={formState.deleteDialogOpen}
        webhookName={formState.webhookToDelete?.name || ''}
        onClose={() => handleClose('deleteDialogOpen')}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default Webhooks;
